import { usersStoreNode } from '@/api/firebase'
import { firebaseObjectToArray, firebaseObjectWithId } from '@/utils'
import { async, GET_ASYNC, ASYNC_MUTATIONS } from '@/utils/async'
import { USERS_STORE } from 'db'

const state = {
    usersStore: false,
    userStore: false,
    ...async()
}

const getters = {
    GET_USERS_STORE: (state) => firebaseObjectToArray(state.usersStore),
    GET_USER_STORE: (state) => firebaseObjectWithId(state.userStore),
    GET_ASYNC
}

const mutations = {
    SET_USERS_STORE(state, payload) {
        state.usersStore = payload
    },
    SET_USER_STORE(state, payload) {
        state.userStore = payload
    },
    CLEAR_USERS_STORE(state) {
        state.usersStore = false
    },
    CLEAR_USER_STORE(state) {
        state.userStore = false
    },
    ...ASYNC_MUTATIONS()
}

const actions = {
    // =================================== C/U/D Operations ===================================

    // =================================== Read Operations ===================================

    /**
     * FETCH_USERS_STORE
     *
     *     Retrieve a list of usersStore and set data in vuex state if not already set.
     *     If empty, set null. Binds a firebase connection.
     *
     * @returns {Void}
     */
    FETCH_USERS_STORE({ commit }) {
        commit('REQUEST')
        commit('CLEAR_USERS_STORE')
        usersStoreNode.on(
            'value',
            function(data) {
                try {
                    commit('SET_USERS_STORE', data.val())
                    commit('SUCCESS')
                } catch (err) {
                    commit('ERROR', err.message)
                }
            },
            function(err) {
                commit('ERROR', err.message)
            }
        )
    },

    /**
     * FETCH_USER_STORE_BY_ID
     *
     *     Retrieve a userStore by id. If empty, set false. Binds a firebase connection.
     *
     * @param payload.id {string} - The id of the store user to retrieve.
     * @returns {Void}
     */
    FETCH_USER_STORE_BY_ID({ commit }, payload) {
        commit('REQUEST')
        commit('CLEAR_USER_STORE')
        usersStoreNode.child(payload.id).on(
            'value',
            function(data) {
                try {
                    commit('SET_USER_STORE', data.val() ? { ...data.val(), id: payload.id } : false)
                    commit('SUCCESS')
                } catch (err) {
                    commit('ERROR', err.message)
                }
            },
            function(err) {
                commit('ERROR', err.message)
            }
        )
    },

    /**
     * FETCH_USER_STORE_BY_FIELD
     *
     *     Retrieve a userStore by a matching field. If empty, set null. Binds a firebase connection.
     *
     * @param payload.key {string} - The name of the field in usersStore to query.
     * @param payload.value {string} - The value of the field in usersStore to match the query.
     * @returns {Void}
     */
    FETCH_USER_STORE_BY_FIELD({ commit }, payload) {
        commit('REQUEST')
        commit('CLEAR_USERS_STORE')
        usersStoreNode
            .orderByChild(payload.key)
            .equalTo(payload.value)
            .on(
                'value',
                function(data) {
                    try {
                        commit('SET_USERS_STORE', data.val())
                        commit('SUCCESS')
                    } catch (err) {
                        commit('ERROR', err.message)
                    }
                },
                function(err) {
                    commit('ERROR', err.message)
                }
            )
    },

    /**
     * FETCH_USER_STORE_BY_ID_ONCE
     *
     *     Retrieve a userStore by id and return the object with the id included as the field 'id'.
     *     If empty, set false. Does not bind a firebase connection.
     *
     * @param payload.shouldReturn {boolean} - Determine if results should be returned and not set on vuex.
     * @param payload.id {string} - The id of the store user to retrieve.
     * @returns {Void / Object}
     */
    async FETCH_USER_STORE_BY_ID_ONCE({ commit }, payload) {
        if (!payload.shouldReturn) {
            commit('REQUEST')
            commit('CLEAR_USER_STORE')
        }
        try {
            const data = (await usersStoreNode.child(payload.id).once('value')).val()
            if (payload.shouldReturn) {
                return data ? { ...data, id: payload.id } : false
            } else {
                commit('SET_USER_STORE', data ? { ...data, id: payload.id } : false)
                commit('SUCCESS')
            }
        } catch (err) {
            if (!payload.shouldReturn) {
                commit('ERROR', err.message)
            } else {
                throw new Error(err.message)
            }
        }
    },

    /**
     * FETCH_USER_STORE_EMAIL_ONCE
     *
     *     Fetch the email of a user by user id.
     *
     * @param payload.id {string} - The id of the user.
     * @returns {string}
     */
    async FETCH_USER_STORE_EMAIL_ONCE(_, payload) {
        return (await usersStoreNode
            .child(payload.id)
            .child(USERS_STORE.fields.email)
            .once('value')).val()
    },

    // =================================== Listener Operations ===================================

    /**
     * REMOVE_USERS_STORE_REFERENCE
     *
     *     Unbind a firebase connection that was set.
     */
    REMOVE_USERS_STORE_REFERENCE({ commit }) {
        commit('CLEAR')
        commit('CLEAR_USERS_STORE')
        usersStoreNode.off()
    },

    /**
     * REMOVE_USER_STORE_REFERENCE
     *
     *     Unbind a firebase connection on a child that was set.
     *
     * @param payload.id {string} - The id of the userStore child.
     */
    REMOVE_USER_STORE_REFERENCE({ commit }, payload) {
        commit('CLEAR')
        commit('CLEAR_USER_STORE')
        usersStoreNode.child(payload.id).off()
    }
}

export { state, getters, mutations, actions }
